import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
  QrCode as QrIcon,
} from '@mui/icons-material';
import { SalesOrder, PaymentEntry } from '../../../../types/sales.types';
import { usePaymentModal as usePaymentModalBase } from '../../../../hooks/usePaymentModal';
import { useSaleFulfillmentDetail } from '../../../../hooks/useSaleFulfillmentDetail';
import { invalidateSaleFulfillmentDetail } from '../../../../services/saleFulfillmentDetailCache';
import { useOrdersPermissions } from '../../../../hooks/useOrdersPermissions';
import { roundMoney } from '../../../../utils/money';
import { formatCurrency as formatCurrencyValue } from '../../../../utils/formatters/money';
import { PAYMENT_METHODS } from '../../../../utils/constants/paymentMethods';
import { PaymentMethod } from '../../../../api';
import { useOtherPaymentMethods } from '../../../../hooks/useOtherPaymentMethods';
import { salesService } from '../../../../services/sales.service';
import { getDeliverableUndeliveredItems } from '../../../../utils/sales/order-delivery.utils';
import { canUpdateSalesOrderDelivery } from '../../../../utils/sales/sales-order-actions';
import { extractErrorMessage } from '../../../../utils/errorHelpers';

const PAYMENT_METHOD_ICONS = {
  CASH: MoneyIcon,
  CARD: CardIcon,
  QR: QrIcon,
};

export interface PaymentFormData {
  paymentAmount: number;
  paymentMethod: string;
  paymentType: 'FULL' | 'PARTIAL';
  changeGiven: number;
  paymentNotes: string;
  cashReceived: number;
  bankReference: string;
  qrTransactionId: string;
}

interface UsePaymentModalLogicProps {
  order: SalesOrder | null;
  onPaymentProcessed?: () => void;
}

function isMixedFulfillmentOrder(order: SalesOrder | null): boolean {
  if (!order?.fulfillmentStatus) return false;
  return order.fulfillmentStatus !== 'NOT_REQUIRED';
}

export const usePaymentModalLogic = ({ order, onPaymentProcessed }: UsePaymentModalLogicProps) => {
  const {
    isLoading,
    isLoadingSummary,
    error,
    success,
    paymentSummary,
    remainingBalance,
    processPayment,
    reportError,
    clearError,
    clearSuccess,
  } = usePaymentModalBase(order, onPaymentProcessed);

  const ordersPerms = useOrdersPermissions();
  const needsFulfillmentDetail = isMixedFulfillmentOrder(order);
  const { detail: fulfillmentDetail } = useSaleFulfillmentDetail(order?.id ?? null, {
    autoFetch: needsFulfillmentDetail,
  });

  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [showPaymentAmountDialog, setShowPaymentAmountDialog] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<string | null>(null);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);

  const { otherPaymentMethods, getPaymentMethodName } = useOtherPaymentMethods();
  const [showOtherMethodsDialog, setShowOtherMethodsDialog] = useState(false);
  const [deliverAllOnComplete, setDeliverAllOnComplete] = useState(true);
  const batchPaymentRef = useRef(false);

  const deliverableUndelivered = useMemo(
    () => (order ? getDeliverableUndeliveredItems(order, fulfillmentDetail) : []),
    [order, fulfillmentDetail],
  );

  const canAutoDeliverOnComplete =
    ordersPerms.canEdit &&
    order != null &&
    canUpdateSalesOrderDelivery(order.status, order.deliveryStatus) &&
    deliverableUndelivered.length > 0;

  useEffect(() => {
    if (order) {
      setPayments([]);
      setPendingPaymentMethod(null);
      setEditingPaymentId(null);
      setDeliverAllOnComplete(true);
    }
  }, [order]);

  const handlePaymentMethodClick = useCallback((method: string) => {
    setPendingPaymentMethod(method);
    setShowPaymentAmountDialog(true);
  }, []);

  const handleOtherMethodSelect = useCallback((method: PaymentMethod) => {
    setPendingPaymentMethod(method.code);
    setShowOtherMethodsDialog(false);
    setShowPaymentAmountDialog(true);
  }, []);

  const handleOpenOtherMethods = useCallback(() => {
    if (otherPaymentMethods.length > 0) {
      setShowOtherMethodsDialog(true);
    }
  }, [otherPaymentMethods.length]);

  const handleCloseOtherMethods = useCallback(() => {
    setShowOtherMethodsDialog(false);
  }, []);

  const handleAddPayment = useCallback(
    (
      method: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
      const currentRemaining = Math.max(0, remainingBalance - totalPayments);
      const paymentAmount = amount > 0 ? amount : Math.max(0, currentRemaining);

      const id = `payment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newPayment: PaymentEntry = {
        id,
        method,
        amount: roundMoney(paymentAmount),
        cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
        bankReference,
        qrTransactionId,
        paymentNotes: notes,
      };

      setPayments((prev) => [...prev, newPayment]);
      setShowPaymentAmountDialog(false);
      setPendingPaymentMethod(null);
    },
    [payments, remainingBalance],
  );

  const handleRemovePayment = useCallback((paymentId: string) => {
    setPayments((prev) => prev.filter((p) => p.id !== paymentId));
  }, []);

  const handleUpdatePaymentAmount = useCallback(
    (
      paymentId: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      setPayments((prev) =>
        prev.map((p) => {
          if (p.id === paymentId) {
            return {
              ...p,
              amount: roundMoney(amount),
              cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
              paymentNotes: notes,
              bankReference,
              qrTransactionId,
            };
          }
          return p;
        }),
      );
      setShowPaymentAmountDialog(false);
      setEditingPaymentId(null);
    },
    [],
  );

  const handleEditPayment = useCallback((paymentId: string) => {
    setEditingPaymentId(paymentId);
    setShowPaymentAmountDialog(true);
  }, []);

  const handleClosePaymentAmountDialog = useCallback(() => {
    setShowPaymentAmountDialog(false);
    setPendingPaymentMethod(null);
    setEditingPaymentId(null);
  }, []);

  const handleProcessPayment = useCallback(async () => {
    if (batchPaymentRef.current || isLoading || payments.length === 0) {
      return;
    }
    batchPaymentRef.current = true;

    try {
      const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
      const willCompletePayment = Math.round((remainingBalance - totalPayments) * 100) / 100 <= 0;
      let batchSucceeded = true;

      for (const payment of payments) {
        const paymentData: PaymentFormData = {
          paymentAmount: payment.amount,
          paymentMethod: payment.method,
          paymentType: 'PARTIAL',
          changeGiven:
            payment.method === PAYMENT_METHODS.CASH &&
            payment.cashReceived != null &&
            payment.cashReceived > payment.amount
              ? roundMoney(payment.cashReceived - payment.amount)
              : 0,
          paymentNotes: payment.paymentNotes?.trim() ?? '',
          cashReceived: payment.cashReceived ?? payment.amount,
          bankReference: payment.bankReference ?? '',
          qrTransactionId: payment.qrTransactionId ?? '',
        };

        const paid = await processPayment(paymentData, { notify: false });
        if (!paid) {
          batchSucceeded = false;
          break;
        }

        // Drop successful charges immediately so a mid-batch failure cannot re-post them.
        setPayments((prev) => prev.filter((p) => p.id !== payment.id));
      }

      if (!batchSucceeded) {
        return;
      }

      if (
        willCompletePayment &&
        deliverAllOnComplete &&
        canAutoDeliverOnComplete &&
        order &&
        deliverableUndelivered.length > 0
      ) {
        try {
          await salesService.updateOrderDelivery(
            order.id,
            deliverableUndelivered.map((item) => ({
              itemId: item.id,
              isDelivered: true,
            })),
          );
        } catch (err: unknown) {
          console.error('Error marking pieces as delivered after payment:', err);
          reportError(
            extractErrorMessage(
              err,
              'El pago se registró, pero no se pudieron marcar las piezas como entregadas',
            ),
          );
        }
      }

      // Single list refresh after payment (+ optional auto-delivery) completes.
      if (order?.id != null) {
        invalidateSaleFulfillmentDetail(order.id);
      }
      onPaymentProcessed?.();
    } finally {
      batchPaymentRef.current = false;
    }
  }, [
    isLoading,
    processPayment,
    payments,
    remainingBalance,
    deliverAllOnComplete,
    canAutoDeliverOnComplete,
    order,
    deliverableUndelivered,
    onPaymentProcessed,
    reportError,
  ]);

  const getPaymentMethodIcon = useCallback((method: string) => {
    return (
      PAYMENT_METHOD_ICONS[method as keyof typeof PAYMENT_METHOD_ICONS] || PAYMENT_METHOD_ICONS.CASH
    );
  }, []);

  const formatCurrency = useCallback((amount: number) => formatCurrencyValue(amount), []);

  const formatDate = useCallback((date: string | Date | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES');
  }, []);

  const dashboardValues = {
    orderTotal:
      paymentSummary?.orderTotal ??
      order?.totalAmount ??
      (order?.totalWithTax as number | undefined) ??
      0,
    totalPaid: paymentSummary?.totalPaid ?? 0,
    remaining: paymentSummary?.remainingBalance ?? remainingBalance,
  };

  const getTotalPayments = useCallback(() => {
    return payments.reduce((sum, p) => sum + p.amount, 0);
  }, [payments]);

  const remainingAfterPayment = Math.round(remainingBalance * 100) / 100 - getTotalPayments();

  return {
    payments,
    isLoading,
    isLoadingSummary,
    error,
    success,
    paymentSummary,
    remainingBalance,
    dashboardValues,
    remainingAfterPayment,
    deliverAllOnComplete,
    setDeliverAllOnComplete,
    deliverableCount: deliverableUndelivered.length,
    canAutoDeliverOnComplete,
    otherPaymentMethods,
    showOtherMethodsDialog,
    showPaymentAmountDialog,
    pendingPaymentMethod,
    editingPaymentId,
    handlePaymentMethodClick,
    handleAddPayment,
    handleRemovePayment,
    handleUpdatePaymentAmount,
    handleEditPayment,
    handleClosePaymentAmountDialog,
    handleProcessPayment,
    handleOtherMethodSelect,
    handleOpenOtherMethods,
    handleCloseOtherMethods,
    clearError,
    clearSuccess,
    getPaymentMethodIcon,
    getPaymentMethodName,
    getTotalPayments,
    formatCurrency,
    formatDate,
  };
};
